import { Component } from 'vue-property-decorator'
import { NegotiationView } from '@/components/forms/view/NegotiationView'
import dayjs from 'dayjs'
import { mapActions } from 'vuex'

@Component({
  methods: {
    ...mapActions('resources/form', ['closeApprovedPurchase']),
  },
})
export class PurchaseView extends NegotiationView {
  closeApprovedPurchase!: ({ idValidator, purchaseId }) => Promise<void>;
  statusPurchaseToConfirm = null
  statusDocument = null
  statusDocumentLegalReview = null
  statusDocumentToUpdate = null
  statusDocumentClose = null
  purchaseOrderDocumentType = null
  statusPaymentOrder = null
  processExpenseBeneficiary = null
  paymentStatus = {
    pending: null,
  }

  consignmentStatus = {
    active: null,
  }

  purchaseOrderStatus = {
    toUpdate: null,
    toUpdateConsignment: null,
    approved: null,
    active: null,
    toConfirm: null,
    toConfirmConsignment: null,
    pickupActive: null,
    pickupToConfirm: null,
    pickupToUpdate: null,
    renewActive: null,
  }

  async mounted () {
    this.paymentStatus.pending = await this.fetchData({
      query: { name: 'find', model: 'ProcessStatus' },
      filter: { _and: [{ process: { table_name: { _eq: 'payment' } } }, { status: { name: { _eq: 'pending' } } }] },
    })

    const documentStatus = await this.fetchData({
      query: { name: 'find', model: 'ProcessStatus' },
      filter: { _and: [{ process: { table_name: { _eq: 'document' } } }] },
    })

    this.statusDocument = documentStatus.filter(status => status.status.name === 'pending')
    this.statusDocumentLegalReview = documentStatus.filter(status => status.status.name === 'legal_review')
    this.statusDocumentToUpdate = documentStatus.filter(status => status.status.name === 'to_update')
    this.statusDocumentClose = documentStatus.filter(status => status.status.name === 'closed')

    this.processExpenseBeneficiary = await this.fetchData({
      query: { name: 'find', model: 'ProcessExpense' },
      filter: { expense: { name: { _eq: 'auto_payment' } } },
    })

    this.statusPaymentOrder = await this.fetchData({
      query: { name: 'find', model: 'ProcessStatus' },
      filter: { _and: [{ process: { table_name: { _eq: 'payment_order' } } }, { status: { name: { _eq: 'pending' } } }] },
    })

    const purchaseOrderStatus = await this.fetchData({
      query: { name: 'find', model: 'ProcessStatus' },
      filter: { _and: [{ process: { table_name: { _eq: 'purchase_order' } } }] },
    })

    this.purchaseOrderStatus.approved = purchaseOrderStatus.filter(status => status.status.name === 'approved')
    this.purchaseOrderStatus.toUpdate = purchaseOrderStatus.filter(status => status.status.name === 'to_update')
    this.purchaseOrderStatus.active = purchaseOrderStatus.filter(status => status.status.name === 'active')
    this.purchaseOrderStatus.toConfirm = purchaseOrderStatus.filter(status => status.status.name === 'to_confirm')
    this.purchaseOrderStatus.pickupActive = purchaseOrderStatus.filter(status => status.status.name === 'pickup_active')
    this.purchaseOrderStatus.pickupToConfirm = purchaseOrderStatus.filter(status => status.status.name === 'pickup_to_confirm')
    this.purchaseOrderStatus.pickupToUpdate = purchaseOrderStatus.filter(status => status.status.name === 'pickup_to_update')
    this.purchaseOrderStatus.renewActive = purchaseOrderStatus.filter(status => status.status.name === 'consignment_active')
    this.purchaseOrderStatus.toConfirmConsignment = purchaseOrderStatus.filter(status => status.status.name === 'consignment_to_confirm')
    this.purchaseOrderStatus.toUpdateConsignment = purchaseOrderStatus.filter(status => status.status.name === 'consignment_to_update')
  }

  async updatePurchase (viewData) {
    const { purchaseOrder, formData } = viewData

    await this.updatePurchaseOrder(viewData)
    await this.updateInterveners(viewData)
    const payments = await this.insertPaymentRecipient(viewData)
    const newPayments = formData.expenses?.payments?.filter(payment => payment?.id).map(pay => {
      return { id: pay.id }
    })
    if (payments?.length) {
      newPayments.push(...payments.map(pay => {
        return { id: pay.id }
      }))
    }
    let newDocument = null
    newDocument = await this.updateDocument(newPayments, viewData)
    await this.insertPurchaseOrderUpdateFile(viewData, purchaseOrder.id, newDocument?.id)
  }

  async updateDocument (payments, viewData) {
    const { purchaseOrder, idProcess, formData } = viewData

    const document = (await this.fetchData({
      query: { name: 'find', model: 'DocumentType' },
      filter: { _and: [{ name: { _eq: 'consignment_contract' } }, { process: { id: { _eq: idProcess } } }] },
    }))[0]

    const existDocument = (await this.fetchData({
      query: { name: 'find', model: 'Document' },
      filter: {
        _and: [
          { id_document_type: { _eq: document?.id } },
          { id_process_record: { _eq: purchaseOrder.id } },
        ],
      },
      force: true,
    }))[0]

    if (existDocument?.id) {
      return this.pushData({
        model: 'Document',
        fields: {
          id: existDocument?.id,
          metadata: { ...existDocument?.metadata, payments, agreementAmount: formData.price },
        },
      })
    }
  }

  async insertPaymentRecipient (viewData) {
    const { formData: { expenses, price }, paymentOrder, payments: fetchPayment } = viewData
    const newPayments = []
    const payments = expenses.payments
      .filter(payment => !payment?.id)

    if (payments.length) {
      await this.pushData({
        model: 'PaymentOrder',
        fields: {
          id: paymentOrder?.[0]?.id,
          amount: price,
        },
      })

      const items = paymentOrder?.[0]?.items
      const expenseMap = this.calculateExpenses(expenses)

      const orderItems = await Promise.all([...expenseMap.entries()].map(async ([name, totalAmount]) => {
        const processExpense = await this.fetchData({
          query: { name: 'find', model: 'ProcessExpense' },
          filter: { expense: { name: { _eq: name } } },
        })

        const orderItemFind = items?.find(item => item.name === name)
        const filteredPayments = orderItemFind?.paymentRecipients?.flatMap(item => item.payments)
        const totalItem = filteredPayments
          ?.filter(pay => !fetchPayment?.map(pay => pay?.id)
            ?.includes(pay?.id))
          ?.reduce((acc, curr) => acc + curr.amount, 0) || 0

        const fields = {
          id: orderItemFind?.id,
          id_payment_order: paymentOrder?.[0]?.id,
          amount: totalAmount + totalItem,
          id_process_expense: processExpense?.[0]?.id,
        }
        if (!fields.id) {
          delete fields.id
        } else {
          delete fields.id_payment_order
          delete fields.id_process_expense
        }

        const orderItem = await this.pushData({
          model: 'PaymentOrderItem',
          fields,
        })

        return {
          name,
          id: orderItem.id,
        }
      }))

      const recipientMap = new Map()

      const recipients = paymentOrder?.[0]?.items?.flatMap(item =>
        item.recipients?.map(recipient => ({
          key: `${recipient.paymentOrder.id}_${recipient.paymentOrderItem.id}_${recipient.person.id}`,
          id: recipient.id,
        })) || []
      )

      for (const item of expenses.payments.filter(payment => !payment?.id)) {
        const orderItem = orderItems.find(oi => oi.name === item.type)

        const recipientKey = `${paymentOrder?.[0].id}_${orderItem.id}_${item?.person?.id}`
        if (!recipients.find(recip => recip.key === recipientKey) && !recipientMap.has(recipientKey)) {
          const recipient = await this.pushData({
            model: 'PaymentRecipient',
            fields: {
              id_payment_order: paymentOrder?.[0]?.id,
              id_payment_order_item: orderItem.id,
              id_person: item?.person?.id,
            },
          })
          recipientMap.set(recipientKey, recipient.id)
        } else if (!recipientMap.has(recipientKey)) {
          recipientMap.set(recipientKey, recipients.find(recip => recip.key === recipientKey).id)
        }
      }

      await Promise.all(expenses.payments.filter(payment => !payment?.id).map(async item => {
        const orderItem = orderItems.find(oi => oi.name === item.type)
        const recipientKey = `${paymentOrder?.[0]?.id}_${orderItem.id}_${item?.person?.id}`
        const recipientId = recipientMap.get(recipientKey)

        const pay = await this.insertPaymentToOrder(viewData, { id: recipientId }, item)
        newPayments.push(pay)
      }))
    }

    return newPayments
  }

  async updateInterveners (viewData) {
    const { document, formData: { signers }, idProcess } = viewData
    const { interveners } = document[0]

    const { deleteInterveners, insertInterveners } = this.getIntervenersAndSigners(interveners, signers)
    if (deleteInterveners.length) {
      await Promise.all(deleteInterveners.map(async id => {
        await this.removeData({
          model: 'Intervener',
          fields: { id },
        })
      }))
    }

    if (insertInterveners.length) {
      this.purchaseOrderDocumentType = await this.fetchData({
        query: { name: 'find', model: 'DocumentType' },
        filter: { _and: [{ name: { _eq: 'sale_purchase_contract' } }, { process: { id: { _eq: idProcess } } }] },
      })

      const fields = this.purchaseOrderDocumentType[0].fields
      const newInterveners = await this.isSellerOrRepresentative(viewData, fields, insertInterveners, true)

      const { id } = document[0]

      await Promise.all(newInterveners.map(async intervener => {
        const fields = {
          id_document: id,
          ...intervener,
        }
        await this.pushData({
          model: 'Intervener',
          fields,
        })
      }))
    }
  }

  getIntervenersAndSigners (interveners, signers) {
    const representative = interveners.filter(intervener => intervener.field.name === 'sale_representative')

    const signersIdSet = new Set(signers.map(signer => signer.id))
    const intervenersPersonIdSet = new Set(representative.map(intervener => intervener.person.id))

    const toDelete = representative.filter(intervener => !signersIdSet.has(intervener.person.id)).map(intervener => intervener.id)
    const toInsert = signers.filter(signer => !intervenersPersonIdSet.has(signer.id))

    return {
      deleteInterveners: toDelete,
      insertInterveners: toInsert,
    }
  }

  async updateConsignment (viewData) {
    const {
      formData: { documentType, agreedTransfer, consignment, price },
      purchaseOrder,
    } = viewData

    if ((purchaseOrder.status.name === 'consignment_active' && !consignment?.length) || purchaseOrder.status.name === 'consignment_to_update') {
      const fields = {
        id: purchaseOrder.id,
        agreed_amount: price,
        id_support_document_type: documentType?.id,
        id_transfer_type: agreedTransfer?.id,
        id_process_status: this.purchaseOrderStatus?.renewActive?.[0]?.id,
      }

      await this.pushData({
        model: 'PurchaseOrder',
        fields,
      })

      if (purchaseOrder?.paymentOrder?.id) {
        await this.pushData({
          model: 'PaymentOrder',
          fields: {
            id: purchaseOrder.paymentOrder.id,
            amount: price,
          },
        })
      }
    } else if (consignment?.length) {
      const fields = {
        id: purchaseOrder.id,
        id_process_status: this.purchaseOrderStatus.toConfirmConsignment[0].id,
        agreed_amount: price,
      }
      await this.pushData({
        model: 'PurchaseOrder',
        fields,
      })
    }
  }

  async updatePurchaseOrder (viewData) {
    const { formData: { price, documentType, agreedTransfer, acquisitionType }, purchaseOrder } = viewData

    await this.pushData({
      model: 'PaymentOrder',
      fields: {
        id: purchaseOrder.paymentOrder.id,
        amount: price,
      },
    })

    if (purchaseOrder.status.isToUpdating && acquisitionType.name === 'consignment') {
      const fields = {
        id: purchaseOrder.id,
        id_acquisition_type: acquisitionType.id,
        agreed_amount: price,
        id_support_document_type: documentType?.id,
        id_transfer_type: agreedTransfer?.id,
        id_process_status: this.purchaseOrderStatus.active?.[0]?.id,
      }

      await this.pushData({
        model: 'PurchaseOrder',
        fields,
      })
    } else if (purchaseOrder.status.isToUpdating && acquisitionType.name === 'purchase') {
      const fields = {
        id: purchaseOrder.id,
        id_acquisition_type: acquisitionType.id,
        agreed_amount: price,
        id_support_document_type: documentType?.id,
        id_transfer_type: agreedTransfer?.id,
        id_process_status: this.purchaseOrderStatus.toConfirm?.[0]?.id,
      }

      await this.pushData({
        model: 'PurchaseOrder',
        fields,
      })
    } else {
      const fields = {
        id: purchaseOrder.id,
        id_process_status: this.purchaseOrderStatus.toConfirm[0].id,
        agreed_amount: price,
      }
      await this.pushData({
        model: 'PurchaseOrder',
        fields,
      })
    }
  }

  async findAssociatedFiles (idProcess, id, name = null) {
    if (!id) return {}

    const report = await this.fetchData({
      query: { name: 'find', model: 'FileProcess' },
      filter: {
        _and: [
          { id_process_record: { _eq: id } },
          { parameter: { process: { id: { _eq: idProcess } } } },
          { parameter: { name: { _eq: 'legal_report' } } }],
      },
      force: true,
    })

    let document

    if (name) {
      document = await this.fetchData({
        query: { name: 'find', model: 'FileProcess' },
        filter: {
          _and: [
            { id_process_record: { _eq: id } },
            { parameter: { process: { id: { _eq: idProcess } } } },
            { parameter: { name: { _eq: name } } }],
        },
        force: true,
      })
    }

    const notarized = await this.fetchData({
      query: { name: 'find', model: 'FileProcess' },
      filter: {
        _and: [
          { id_process_record: { _eq: id } },
          { parameter: { process: { id: { _eq: idProcess } } } },
          { parameter: { name: { _eq: 'notarized_authorization' } } }],
      },
      force: true,
    })

    const alimonyCertificate = await this.fetchData({
      query: { name: 'find', model: 'FileProcess' },
      filter: {
        _and: [
          { id_process_record: { _eq: id } },
          { parameter: { process: { id: { _eq: idProcess } } } },
          { parameter: { name: { _eq: 'alimony_certificate' } } }],
      },
      force: true,
    })

    const commercialReport = await this.fetchData({
      query: { name: 'find', model: 'FileProcess' },
      filter: {
        _and: [
          { id_process_record: { _eq: id } },
          { parameter: { process: { id: { _eq: idProcess } } } },
          { parameter: { name: { _eq: 'commercial_report' } } }],
      },
      force: true,
    })

    return {
      report,
      document,
      notarized,
      alimonyCertificate,
      commercialReport,
    }
  }

  async createPurchase (viewData) {
    const {
      formData: { price, documentType, agreedTransfer, acquisitionType },
      negotiation,
      idProcess,
    } = viewData

    const idStatus = acquisitionType.name === 'consignment'
      ? this.purchaseOrderStatus.active?.[0]?.id : this.purchaseOrderStatus.toConfirm?.[0]?.id

    const fields = {
      agreed_amount: price,
      id_acquisition_type: acquisitionType.id,
      id_negotiation: negotiation.id,
      id_process_status: idStatus,
      id_support_document_type: documentType?.id,
      id_transfer_type: agreedTransfer?.id,
    }

    const purchase = await this.pushData({
      model: 'PurchaseOrder',
      fields,
    })

    this.purchaseOrderDocumentType = await this.fetchData({
      query: { name: 'find', model: 'DocumentType' },
      filter: { _and: [{ name: { _eq: 'sale_purchase_contract' } }, { process: { id: { _eq: idProcess } } }] },
    })

    const purchaseOrderDocumentTypeConsignment = await this.fetchData({
      query: { name: 'find', model: 'DocumentType' },
      filter: { _and: [{ name: { _eq: 'consignment_contract' } }, { process: { id: { _eq: idProcess } } }] },
    })
    let newDocument = null
    const payments = await this.insertPaymentOrder(viewData, purchase.id)
    if (acquisitionType.name === 'consignment') {
      newDocument = await this.insertDocument(payments, viewData, purchase.id, purchaseOrderDocumentTypeConsignment[0], true)
    }

    await this.insertPurchaseOrderUpdateFile(viewData, purchase.id, newDocument?.id)

    await this.insertDocument([], viewData, purchase.id, this.purchaseOrderDocumentType[0])

    await this.pushData({
      model: 'Negotiation',
      fields: {
        id: negotiation.id,
        id_process_status: this.statusNegotiation.closed?.[0]?.id,
        id_closing_reason: this.closingReasonNegotiation.success?.[0]?.id,
      },
    })
  }

  async insertPurchaseOrderUpdateFile (viewData, id, idDocument = null) {
    const {
      idProcess,
      idProcessDocument,
      formData: {
        legalReport,
        buyFile,
        proofPayment,
        notarizedAuthorization,
        linkLegalReport,
        linkContract,
        consignment,
        alimonyCertificate,
        commercialReport,
      },
      fields: {
        legalReport: legalReportField,
        buyFile: buyFileField,
        proofPayment: proofPaymentField,
        notarizedAuthorization: notarizedAuthorizationField,
        consignment: consignmentField,
        alimonyCertificate: alimonyCertificateField,
        commercialReport: commercialReportField,
      },
    } = viewData

    if (legalReport?.length) {
      await this.handleFileType(legalReport, legalReportField, idProcess, id)
      const fileId = this.isArrayFiles(legalReport) ? legalReport[0]?.id : legalReport?.[0]?.file?.id
      await this.insertFileInfo({ id: fileId, source_link: linkLegalReport })
    }
    if (buyFile?.length) {
      await this.handleFileType(buyFile, buyFileField, idProcess, id)

      if (buyFileField?.properties?.name === 'digital_contract') {
        const fileId = this.isArrayFiles(buyFile) ? buyFile[0]?.id : buyFile?.[0]?.file?.id
        await this.insertFileInfo({ id: fileId, source_link: linkContract })
      }
    }

    if (proofPayment?.length) {
      await this.handleFileType(proofPayment, proofPaymentField, idProcess, id)
    }

    if (notarizedAuthorization?.length) {
      await this.handleFileType(notarizedAuthorization, notarizedAuthorizationField, idProcess, id)
    }

    if (consignment?.length) {
      await this.handleFileType(consignment, consignmentField, idProcessDocument, idDocument)
    }

    if (alimonyCertificate?.length) {
      await this.handleFileType(alimonyCertificate, alimonyCertificateField, idProcess, id)
    }

    if (commercialReport?.length) {
      await this.handleFileType(commercialReport, commercialReportField, idProcess, id)
    }
  }

  async insertDocument (payments = [], viewData, id, documentType, metadata = false) {
    const { statusDocument } = this
    const { purchaseOrder, dealAutoAttribute, formData } = viewData

    const sellerOrRepresentative = await this.isSellerOrRepresentative(viewData, documentType.fields)

    const fields = {
      id_process_status: statusDocument?.[0]?.id,
      id_document_type: documentType?.id,
      id_process_record: id,
      date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      interveners: {
        data: [...sellerOrRepresentative],
      },
      metadata: {},
    }

    if (metadata) {
      const nonWithdrawalCost = documentType?.fields?.find(field => field.name === 'non_withdrawal_cost')
      const withdrawalPeriod = documentType?.fields?.find(field => field.name === 'withdrawal_period')
      const earlyWithdrawalPenalty = documentType?.fields?.find(field => field.name === 'early_withdrawal_penalty')
      const consignmentPeriod = documentType?.fields?.find(field => field.name === 'consignment_period')

      fields.metadata = {
        consignment_period: {
          days: consignmentPeriod.structure.days,
        },
        auto: purchaseOrder.negotiation.auto.version.version.title,
        year: purchaseOrder.negotiation.auto.year,
        ppu: purchaseOrder.negotiation.auto.ppu,
        withdrawal_period: {
          days: withdrawalPeriod.structure.days,
        },
        non_withdrawal_cost: {
          amount: nonWithdrawalCost.structure.amount,
        },
        early_withdrawal_penalty: {
          amount: earlyWithdrawalPenalty.structure.amount,
        },
        mileage: dealAutoAttribute.value,
        agreementAmount: formData.price,
      }
      if (payments.length) {
        fields.metadata = {
          ...fields.metadata,
          payments,
        }
      }
    } else {
      fields.metadata = {
        auto: purchaseOrder.negotiation.auto.version.version.title,
        year: purchaseOrder.negotiation.auto.year,
        ppu: purchaseOrder.negotiation.auto.ppu,
        mileage: dealAutoAttribute.value,
      }
    }

    return this.pushData({
      model: 'Document',
      fields,
    })
  }

  async insertDocumentConsignmentWithdrawalContract (viewData, id, documentType, metadata = false) {
    const { statusDocument } = this
    const { formData } = viewData

    const existDocument = (await this.fetchData({
      query: { name: 'find', model: 'Document' },
      filter: {
        _and: [
          { id_document_type: { _eq: documentType?.id } },
          { id_process_record: { _eq: id } },
        ],
      },
      force: true,
    }))[0]

    if (!existDocument?.id) {
      const sellerOrRepresentative = await this.isSellerOrRepresentative(viewData, documentType.fields)

      const fields = {
        id_process_status: statusDocument?.[0]?.id,
        id_document_type: documentType?.id,
        id_process_record: id,
        date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        interveners: {
          data: [...sellerOrRepresentative],
        },
        metadata: {},
      }

      if (!metadata) {
        fields.metadata = {
          mileage: formData.mileage,
          cost: formData.cost,
          hasCost: formData.hasCost,
        }
      }

      return this.pushData({
        model: 'Document',
        fields,
      })
    } else {
      const fields = {
        id: existDocument.id,
        metadata: {
          mileage: formData.mileage,
          cost: formData.cost,
          hasCost: formData.hasCost,
        },
      }

      await this.pushData({
        model: 'Document',
        fields,
      })
    }

    return existDocument
  }

  async processSigner (signer, isSeller, fields, address = false) {
    let field
    let personAddress
    if (address && signer?.id) {
      personAddress = (await this.fetchData({
        query: { name: 'find', model: 'PersonAddress' },
        filter: {
          _and: [
            { person: { id: { _eq: signer?.id } } },
            { default: { _eq: true } },
            { active: { _eq: true } },
          ],
        },
        force: true,
      }))[0]
    }

    if (isSeller) {
      field = {
        id_person: isSeller?.id,
        uid: isSeller.uid,
        business_name: isSeller.fullName,
        id_person_address: personAddress?.id,
        business_domicile: personAddress?.address?.fullAddress,
        id_field: fields.find(field => field.name === 'seller')?.id,
      }
    } else {
      field = {
        id_person: signer.id,
        business_name: signer.fullName,
        uid: signer.uid,
        id_person_address: personAddress?.id,
        business_domicile: personAddress?.address?.fullAddress,
        id_field: fields.find(field => field.name === 'sale_representative')?.id,
      }
    }

    return field
  }

  async isSellerOrRepresentative (viewData, fields, insertSigners = [], update = false): Promise<Record<string, any>[]> {
    const { formData: { owners, signers } } = viewData

    const getSigners = insertSigners?.length ? insertSigners : signers

    const newSignersPromises = getSigners?.map(signer => this.processSigner(signer, null, fields)) || []
    const oldOwnersPromises = owners?.map(owner => this.processSigner(null, owner, fields, true)) || []

    // Wait for all Promises to resolve
    const newSigners = await Promise.all(newSignersPromises)
    const oldOwners = await Promise.all(oldOwnersPromises)

    if (update) {
      return [...newSigners].filter(Boolean)
    }

    return [...newSigners, ...oldOwners].filter(Boolean)
  }

  calculateExpenses (expenses): Map<string, number> {
    const expenseMap = new Map()

    if (!expenses?.payments?.length) return expenseMap

    const payments = expenses?.payments?.filter(payment => !payment?.id)

    payments.forEach(item => {
      const itemName = item.type
      const amount = parseInt(item?.amount) || 0

      if (expenseMap.has(itemName)) {
        expenseMap.set(itemName, expenseMap.get(itemName) + amount)
      } else {
        expenseMap.set(itemName, amount)
      }
    })

    return expenseMap
  }

  async insertPaymentOrder (viewData, id) {
    const { formData: { expenses, price, acquisitionType }, negotiation, idProcess } = viewData

    const payments = []
    const fields = {
      id_process: idProcess,
      id_deal: negotiation.inspection.appraisal.deal.id,
      id_process_record: id,
      id_acquisition_type: acquisitionType.id,
      id_person: negotiation.inspection.appraisal.deal.lead.client.id,
      amount: price,
    }

    const order = await this.pushData({
      model: 'PaymentOrder',
      fields,
    })

    const expenseMap = this.calculateExpenses(expenses)

    const orderItems = await Promise.all([...expenseMap.entries()].map(async ([name, totalAmount]) => {
      const processExpense = await this.fetchData({
        query: { name: 'find', model: 'ProcessExpense' },
        filter: { expense: { name: { _eq: name } } },
      })

      const orderItem = await this.pushData({
        model: 'PaymentOrderItem',
        fields: {
          id_payment_order: order.id,
          amount: totalAmount,
          id_process_expense: processExpense?.[0]?.id,
        },
      })

      return {
        name,
        id: orderItem.id,
      }
    }))

    const recipientMap = new Map()

    for (const item of expenses.payments) {
      const orderItem = orderItems.find(oi => oi.name === item.type)
      const recipientKey = `${order.id}_${orderItem.id}_${item?.person?.id}`

      if (!recipientMap.has(recipientKey)) {
        const recipient = await this.pushData({
          model: 'PaymentRecipient',
          fields: {
            id_payment_order: order.id,
            id_payment_order_item: orderItem.id,
            id_person: item?.person?.id,
          },
        })
        recipientMap.set(recipientKey, recipient.id)
      }
    }

    await Promise.all(expenses.payments.map(async item => {
      const orderItem = orderItems.find(oi => oi.name === item.type)
      const recipientKey = `${order.id}_${orderItem.id}_${item?.person?.id}`
      const recipientId = recipientMap.get(recipientKey)

      const pay = await this.insertPaymentToOrder(viewData, { id: recipientId }, item)

      payments.push(pay)
    }))

    return payments
  }

  async insertPaymentToOrder (viewData, recipient, item) {
    const { negotiation } = viewData
    const { paymentStatus: { pending } } = this

    const type = await this.fetchData({
      query: { name: 'find', model: 'PaymentType' },
      filter: { name: { _eq: 'electronic_transfer' } },
    })

    const fields = {
      id_process_status: pending[0].id,
      id_payment_type: item?.paymentType?.id || type?.[0]?.id,
      id_payment_recipient: recipient.id,
      amount: item?.amount,
      comment: item?.paymentRecipient?.payments[0]?.comment,
      id_deal: negotiation?.inspection.appraisal.deal.id,
      id_recipient_account: item?.account?.id || item?.recipients?.[0]?.payments?.recipientAccount?.id || null,
    }
    const newPayment = await this.pushData({
      model: 'Payment',
      fields,
    })
    const file = item?.backup?.length ? item.backup : item?.paymentBackup
    if (file?.length) {
      const process = await this.fetchData({
        query: { name: 'find', model: 'Process' },
        filter: { table_name: { _eq: 'payment' } },
      })
      const fileInfo = await this.fetchData({
        query: { name: 'find', model: 'FileParameter' },
        filter: { _and: [{ process: { table_name: { _eq: 'payment' } } }, { name: { _eq: 'spending_support' } }] },
      })
      const backup = {
        properties: {
          required: true,
          properties: {
            label: fileInfo[0].description,
            multiple: fileInfo[0].multiple,
            accept: fileInfo[0].fileType.mimes,
            fileTypeId: fileInfo[0].fileType.id,
            name: fileInfo[0].name,
            required: fileInfo[0].required,
          },
        },
      }
      await this.handleFileType(file, { properties: backup }, process[0].id, newPayment.id, fileInfo)
    }

    return newPayment
  }

  async insertPayment (viewData, recipient) {
    const { formData: { expenses }, negotiation } = viewData
    const { paymentStatus: { pending } } = this

    const payments = expenses.payments.filter(payment => payment.person.id === recipient.person.id && !payment?.id)

    if (payments.length) {
      return Promise.all(payments.map(async payment => {
        const fields = {
          id_process_status: pending[0].id,
          id_payment_type: payment?.paymentType?.id,
          id_payment_recipient: recipient.id,
          amount: payment?.amount,
          id_deal: negotiation?.inspection.appraisal.deal.id,
          id_recipient_account: payment?.account?.id || null,
        }
        await this.pushData({
          model: 'Payment',
          fields,
        })
      }))
    }
  }

  async removeRecipient (item) {
    await this.removeData({
      model: 'PaymentRecipient',
      fields: { id: item.id },
    })
  }

  findLocalPos (recipient, val) {
    if (recipient?.id) return false
    return recipient?.person?.id === val?.beneficiary?.id &&
      recipient.payments.find(payment =>
        (payment?.recipientAccount?.id && val?.account?.id && payment?.recipientAccount?.id === val?.account?.id) ||
        (payment?.type?.id && val?.paymentType?.name === 'ok_view' && payment?.type?.id === val?.paymentType?.id) ||
        (payment?.amount === val?.amount)
      )
  }

  findLocalPayment (payment, person, type, val) {
    return !payment.id && type?.name === val?.type &&
      ((val?.account?.id && payment?.recipientAccount?.id === val?.account?.id) ||
        (val?.paymentType?.id && payment?.type?.id === val?.paymentType?.id) ||
        (val?.paymentRecipient?.person?.id === person.id && val?.amount === payment?.amount))
  }

  async updatePaymentOrder (viewData) {
    const { paymentOrder } = viewData

    const existingPayments = await this.fetchExistingPayments(paymentOrder[0]?.id)

    await this.cancelPayments(existingPayments)
  }

  // Función auxiliar para obtener pagos existentes
  async fetchExistingPayments (orderId) {
    if (!orderId) return []
    return this.fetchData({
      query: { name: 'find', model: 'Payment' },
      filter: { payment_recipient: { id_payment_order: { _eq: orderId } } },
    })
  }

  // Función auxiliar para cancelar pagos
  async cancelPayments (paymentsToCancel) {
    const status = await this.fetchData({
      query: { name: 'find', model: 'ProcessStatus' },
      filter: { _and: [{ process: { table_name: { _eq: 'payment' } } }, { status: { name: { _eq: 'closed' } } }] },
    })

    const filterSuccess = {
      _and: [
        { type: { name: { _eq: 'canceled' } } },
        { status: { process: { table_name: { _eq: 'payment' } } } },
      ],
    }

    const closingReason = await this.fetchData({
      query: { name: 'find', model: 'ClosingReason' },
      filter: filterSuccess,
    })
    await Promise.all(
      paymentsToCancel.map(async payment => {
        await this.pushData({
          model: 'Payment',
          fields: {
            id: payment.id,
            id_process_status: status[0].id,
            id_closing_reason: closingReason[0].id,
          },
        })
      })
    )
  }

  // Función auxiliar para crear nuevos pagos
  async createPayments (viewData) {
    const { paymentOrder, formData: { expenses } } = viewData

    const recipient = paymentOrder[0]?.items[0]?.recipients[0]
    const newPayments = []

    for (const item of expenses) {
      const newPayment = await this.insertPaymentToOrder(viewData, recipient, item)
      newPayments.push(newPayment)
    }
    return newPayments
  }
}
